import React, { useEffect, useState } from 'react';
import {
    UserCircleIcon,
    XMarkIcon,
    PlusIcon,
    MagnifyingGlassIcon,
    ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { doc, getDoc, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import DashboardWrapper from '../../components/layout/DashboardWrapper';
import { Employee, EmployeeStatus } from '../../types/hr';
import {
    fetchEmployees,
    updateEmployee,
} from '../../services/hrService';
import { db, functions } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { ALL_PAGE_PERMISSIONS } from '../../auth/adminAccess';

const ALL_PERMISSIONS = ALL_PAGE_PERMISSIONS;

interface AdminAccess {
    permissions: string[];
    superAdmin: boolean;
    enabled: boolean;
}

interface PermissionsModalProps {
    employeeName: string;
    isSelf: boolean;
    access: AdminAccess;
    onClose: () => void;
    onSave: (permissions: string[], superAdmin: boolean) => Promise<void>;
}

const PermissionsModal: React.FC<PermissionsModalProps> = ({ employeeName, isSelf, access, onClose, onSave }) => {
    const [permissions, setPermissions] = useState<string[]>(access.permissions);
    const [superAdmin, setSuperAdmin] = useState(access.superAdmin);
    const [saving, setSaving] = useState(false);

    const toggle = (key: string) => {
        setPermissions((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave(permissions, superAdmin);
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
                    <div>
                        <h2 className="text-base font-semibold">Edit Permissions</h2>
                        <p className="text-xs text-gray-500 mt-0.5">{employeeName}</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div>
                            <div className="text-sm font-semibold">Super Admin</div>
                            <div className="text-xs text-gray-500 mt-0.5">Full access to all pages and settings</div>
                        </div>
                        <button
                            onClick={() => setSuperAdmin((v) => !v)}
                            disabled={isSelf}
                            title={isSelf ? "You cannot change your own super admin status" : undefined}
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${superAdmin ? 'bg-gray-800' : 'bg-gray-200'}`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${superAdmin ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                    </div>

                    <div className="flex flex-col gap-1">
                        <div className="text-xs font-medium text-gray-500 uppercase mb-1">Page Access</div>
                        {ALL_PERMISSIONS.map(({ key, label }) => {
                            const enabled = superAdmin || permissions.includes(key);
                            return (
                                <div key={key} className="flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                                    <span className={`text-sm ${superAdmin ? 'text-gray-400' : 'text-gray-700'}`}>{label}</span>
                                    <button
                                        onClick={() => !superAdmin && toggle(key)}
                                        disabled={superAdmin}
                                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:cursor-not-allowed ${enabled ? 'bg-gray-800' : 'bg-gray-200'}`}
                                    >
                                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex gap-2 px-5 py-4 border-t shrink-0">
                    <button onClick={onClose} className="flex-1 py-2 text-sm border rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                    <button onClick={handleSave} disabled={saving || isSelf} className="flex-1 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const STATUS_LABELS: Record<EmployeeStatus, string> = {
    active: 'Active',
    inactive: 'Inactive',
    terminated: 'Terminated',
};

const STATUS_CLASSES: Record<EmployeeStatus, string> = {
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-yellow-100 text-yellow-700',
    terminated: 'bg-red-100 text-red-700',
};

const EMPTY_FORM: Omit<Employee, 'id' | 'createdAt' | 'updatedAt'> = {
    employeeId: '',
    firstName: '',
    surname: '',
    email: '',
    department: '',
    jobTitle: '',
    startDate: undefined,
    status: 'active',
};

const EmployeesPage: React.FC = () => {
    const { currentUser } = useAuth();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [adminAccess, setAdminAccess] = useState<AdminAccess | null>(null);
    const [loadingAdmin, setLoadingAdmin] = useState(false);
    const [togglingAdmin, setTogglingAdmin] = useState(false);
    const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [modalEmployee, setModalEmployee] = useState<Omit<Employee, 'id' | 'createdAt' | 'updatedAt'>>(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    const loadEmployees = async () => {
        setLoading(true);
        try {
            const data = await fetchEmployees();
            setEmployees(data);
        } catch (err) {
            console.error('[EmployeesPage] Failed to load employees:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmployees();
    }, []);

    // uid field is written by createEmployee Cloud Function; fall back to doc ID if absent
    const selectedEmployeeUid = selectedEmployee ? (selectedEmployee.uid ?? selectedEmployee.id) : null;

    useEffect(() => {
        if (!selectedEmployeeUid) {
            setAdminAccess(null);
            return;
        }
        setLoadingAdmin(true);
        getDoc(doc(db, 'employees', selectedEmployeeUid))
            .then((snap) => {
                if (snap.exists()) {
                    const d = snap.data();
                    setAdminAccess({
                        permissions: Array.isArray(d.permissions) ? d.permissions : [],
                        superAdmin: d.superAdmin === true,
                        enabled: d.enabled !== false,
                    });
                } else {
                    setAdminAccess(null);
                }
            })
            .catch((err) => console.error('[EmployeesPage] Failed to load admin access:', err))
            .finally(() => setLoadingAdmin(false));
    }, [selectedEmployeeUid]);

    const handleToggleAdmin = async () => {
        if (!selectedEmployeeUid) return;
        setTogglingAdmin(true);
        try {
            if (adminAccess) {
                await deleteDoc(doc(db, 'employees', selectedEmployeeUid));
                setAdminAccess(null);
            } else {
                const newAccess: AdminAccess = { permissions: [], superAdmin: false, enabled: true };
                await setDoc(doc(db, 'employees', selectedEmployeeUid), newAccess);
                setAdminAccess(newAccess);
            }
        } catch (err) {
            console.error('[EmployeesPage] Failed to toggle admin access:', err);
        } finally {
            setTogglingAdmin(false);
        }
    };

    const handleSavePermissions = async (permissions: string[], superAdmin: boolean) => {
        if (!selectedEmployeeUid || selectedEmployeeUid === currentUser?.uid) {
            throw new Error('You cannot change your own admin grants');
        }
        await updateDoc(doc(db, 'employees', selectedEmployeeUid), { permissions, superAdmin });
        setAdminAccess((prev) => prev ? { ...prev, permissions, superAdmin } : null);
    };

    const filteredEmployees = employees.filter((e) => {
        const term = searchTerm.toLowerCase();
        return (
            e.firstName.toLowerCase().includes(term) ||
            e.surname.toLowerCase().includes(term) ||
            e.email.toLowerCase().includes(term) ||
            e.employeeId.toLowerCase().includes(term) ||
            e.department.toLowerCase().includes(term)
        );
    });

    const openAddModal = () => {
        setModalMode('add');
        setModalEmployee(EMPTY_FORM);
        setModalError(null);
        setIsModalOpen(true);
    };

    const openEditModal = (emp: Employee) => {
        setModalMode('edit');
        setModalEmployee({
            employeeId: emp.employeeId,
            firstName: emp.firstName,
            surname: emp.surname,
            email: emp.email,
            department: emp.department,
            jobTitle: emp.jobTitle,
            startDate: emp.startDate,
            status: emp.status,
        });
        setModalError(null);
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!modalEmployee.firstName.trim() || !modalEmployee.surname.trim() || !modalEmployee.email.trim()) {
            setModalError('First name, surname, and email are required.');
            return;
        }
        setSaving(true);
        setModalError(null);
        try {
            if (modalMode === 'add') {
                const callCreateEmployee = httpsCallable(functions, 'createEmployee');
                await callCreateEmployee({
                    email: modalEmployee.email.trim(),
                    firstName: modalEmployee.firstName.trim(),
                    surname: modalEmployee.surname.trim(),
                    department: modalEmployee.department,
                    jobTitle: modalEmployee.jobTitle,
                    employeeId: modalEmployee.employeeId,
                    startDate: modalEmployee.startDate?.toISOString() ?? null,
                    status: modalEmployee.status,
                });
            } else if (selectedEmployee) {
                const { status, ...profile } = modalEmployee;
                await updateEmployee(selectedEmployee.id, profile);
                if (status === 'terminated' && selectedEmployee.status !== 'terminated') {
                    const callTerminateEmployee = httpsCallable(functions, 'terminateEmployee');
                    await callTerminateEmployee({ employeeId: selectedEmployee.id });
                } else if (status !== selectedEmployee.status) {
                    await updateEmployee(selectedEmployee.id, { status });
                }
                setSelectedEmployee({ ...selectedEmployee, ...modalEmployee, status });
            }
            setIsModalOpen(false);
            await loadEmployees();
        } catch (err: unknown) {
            console.error('[EmployeesPage] Save failed:', err);
            const msg = (err as { message?: string })?.message ?? 'Failed to save. Please try again.';
            setModalError(msg);
        } finally {
            setSaving(false);
        }
    };

    const totalActive = employees.filter((e) => e.status === 'active').length;
    const totalTerminated = employees.filter((e) => e.status === 'terminated').length;

    return (
        <DashboardWrapper>
            <div className="flex h-[calc(100vh-64px)] overflow-hidden">
                {/* Main panel */}
                <div className="flex-1 flex flex-col gap-6 p-6 min-w-0 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between flex-shrink-0">
                        <div>
                            <h1 className="text-xl font-semibold">Employee Management</h1>
                            <p className="text-sm text-gray-500 mt-0.5">Manage internal company staff for ISO 27001 compliance</p>
                        </div>
                        <button
                            onClick={openAddModal}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-black transition-colors"
                        >
                            <PlusIcon className="h-4 w-4" />
                            Add
                        </button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4 flex-shrink-0">
                        <div className="bg-white border rounded-xl p-4">
                            <p className="text-sm text-gray-500">Total Employees</p>
                            <p className="text-2xl font-semibold mt-1">{employees.length}</p>
                        </div>
                        <div className="bg-blue-50 border-blue-100 border rounded-xl p-4">
                            <p className="text-sm text-blue-500">Active</p>
                            <p className="text-2xl font-semibold mt-1 text-blue-700">{totalActive}</p>
                        </div>
                        <div className="bg-red-100 border-red-200 border rounded-xl p-4">
                            <p className="text-sm text-red-500">Terminated</p>
                            <p className="text-2xl font-semibold mt-1 text-red-700">{totalTerminated}</p>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="flex-shrink-0">
                        <div className="relative max-w-xl">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, department..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                        </div>
                    </div>

                    <div className="flex flex-1 min-h-0 bg-white rounded-xl border overflow-hidden flex-col">
                        {/* Table header */}
                        <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider flex-shrink-0">
                            <div className="col-span-3">Name</div>
                            <div className="col-span-3">Email</div>
                            <div className="col-span-3">Job Title</div>
                            <div className="col-span-2">Department</div>
                            <div className="col-span-1">Status</div>
                        </div>

                        {/* Table rows */}
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {loading ? (
                                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
                                    Loading employees...
                                </div>
                            ) : filteredEmployees.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-32 text-gray-400 text-sm gap-1">
                                    <UserCircleIcon className="h-8 w-8 text-gray-300" />
                                    <span>{searchTerm ? 'No employees match your search' : 'No employees yet — add your first one'}</span>
                                </div>
                            ) : (
                                filteredEmployees.map((emp) => {
                                    const isSelected = selectedEmployee?.id === emp.id;
                                    return (
                                        <div
                                            key={emp.id}
                                            onClick={() => setSelectedEmployee(isSelected ? null : emp)}
                                            className={`grid grid-cols-12 items-center gap-4 px-6 py-3 border-b cursor-pointer transition-colors text-sm ${
                                                isSelected
                                                    ? 'bg-primary/5 border-l-2 border-l-primary'
                                                    : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="col-span-3 font-medium truncate">{emp.firstName} {emp.surname}</div>
                                            <div className="col-span-3 text-gray-500 truncate">{emp.email}</div>
                                            <div className="col-span-3 text-gray-600 truncate">{emp.jobTitle || ''}</div>
                                            <div className="col-span-2 text-gray-600 truncate">{emp.department || ''}</div>
                                            <div className="col-span-1">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] uppercase font-medium ${STATUS_CLASSES[emp.status]}`}>
                                                    {STATUS_LABELS[emp.status]}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>



                {/* Detail sidebar */}
                {selectedEmployee && (
                    <div className="w-[400px] border-l bg-white flex flex-col overflow-hidden flex-shrink-0">
                        {/* Sidebar header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white text-sm font-semibold flex-shrink-0">
                                    {selectedEmployee.firstName[0]}{selectedEmployee.surname[0]}
                                </div>
                                <div className="flex flex-col items-start gap-1">
                                    <p className="font-semibold text-sm truncate">{selectedEmployee.firstName} {selectedEmployee.surname}</p>
                                    <span className="text-[10px] font-medium uppercase text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">{selectedEmployee.employeeId || 'No ID'}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedEmployee(null)}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto min-h-0 p-5 flex flex-col gap-5">
                            {/* Info rows */}
                            <div className="flex flex-col gap-3">
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Details</h3>
                                {[
                                    { label: 'Email', value: selectedEmployee.email },
                                    { label: 'Department', value: selectedEmployee.department || '-' },
                                    { label: 'Job Title', value: selectedEmployee.jobTitle || '-' },
                                    {
                                        label: 'Start Date',
                                        value: selectedEmployee.startDate
                                            ? selectedEmployee.startDate.toLocaleDateString()
                                            : '-',
                                    },
                                    {
                                        label: 'Status',
                                        value: (
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] uppercase font-medium ${STATUS_CLASSES[selectedEmployee.status]}`}>
                                                {STATUS_LABELS[selectedEmployee.status]}
                                            </span>
                                        ),
                                    },
                                ].map(({ label, value }) => (
                                    <div key={label} className="flex items-start gap-2 text-sm">
                                        <span className="text-gray-400 w-24 flex-shrink-0">{label}</span>
                                        <span className="text-gray-800 flex-1">{value}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Edit button */}
                            <button
                                onClick={() => openEditModal(selectedEmployee)}
                                className="w-full px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Edit Employee
                            </button>

                            {/* Admin Access */}
                            <div>
                                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Admin Access</h3>
                                {loadingAdmin ? (
                                    <p className="text-sm text-gray-400">Loading...</p>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                            <div>
                                                <p className="text-sm font-medium">Admin Portal Access</p>
                                                <p className="text-xs text-gray-400 mt-0.5">
                                                    {adminAccess
                                                        ? adminAccess.superAdmin
                                                            ? 'Super Admin'
                                                            : `${adminAccess.permissions.length} permission${adminAccess.permissions.length !== 1 ? 's' : ''}`
                                                        : 'No access'}
                                                </p>
                                            </div>
                                            <button
                                                onClick={handleToggleAdmin}
                                                disabled={togglingAdmin || selectedEmployeeUid === currentUser?.uid}
                                                title={selectedEmployeeUid === currentUser?.uid ? 'You cannot remove your own access' : undefined}
                                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${adminAccess ? 'bg-gray-800' : 'bg-gray-200'}`}
                                            >
                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${adminAccess ? 'translate-x-6' : 'translate-x-1'}`} />
                                            </button>
                                        </div>
                                        {adminAccess && (
                                            <button
                                                onClick={() => setIsPermissionsModalOpen(true)}
                                                className="w-full px-4 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <ShieldCheckIcon className="h-4 w-4" />
                                                Edit Permissions
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {isPermissionsModalOpen && selectedEmployee && adminAccess && (
                <PermissionsModal
                    employeeName={`${selectedEmployee.firstName} ${selectedEmployee.surname}`}
                    isSelf={selectedEmployeeUid === currentUser?.uid}
                    access={adminAccess}
                    onClose={() => setIsPermissionsModalOpen(false)}
                    onSave={handleSavePermissions}
                />
            )}

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h2 className="text-base font-semibold">
                                {modalMode === 'add' ? 'Add Employee' : 'Edit Employee'}
                            </h2>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="btn btn-ghost btn-sm btn-square"
                            >
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="px-6 py-5 flex flex-col gap-4 overflow-y-auto max-h-[70vh]">
                            {modalError && (
                                <div className="p-3 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg flex justify-between items-center">
                                    <span>{modalError}</span>
                                    <button onClick={() => setModalError(null)}>
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                                    <input
                                        type="text"
                                        value={modalEmployee.firstName}
                                        onChange={(e) => setModalEmployee((p) => ({ ...p, firstName: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Surname *</label>
                                    <input
                                        type="text"
                                        value={modalEmployee.surname}
                                        onChange={(e) => setModalEmployee((p) => ({ ...p, surname: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input
                                    type="email"
                                    value={modalEmployee.email}
                                    onChange={(e) => setModalEmployee((p) => ({ ...p, email: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                                    <input
                                        type="text"
                                        value={modalEmployee.department}
                                        onChange={(e) => setModalEmployee((p) => ({ ...p, department: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                                    <input
                                        type="text"
                                        value={modalEmployee.jobTitle}
                                        onChange={(e) => setModalEmployee((p) => ({ ...p, jobTitle: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                                    <input
                                        type="text"
                                        placeholder="e.g. EMP-042"
                                        value={modalEmployee.employeeId}
                                        onChange={(e) => setModalEmployee((p) => ({ ...p, employeeId: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        value={modalEmployee.startDate
                                            ? modalEmployee.startDate.toISOString().split('T')[0]
                                            : ''}
                                        onChange={(e) =>
                                            setModalEmployee((p) => ({
                                                ...p,
                                                startDate: e.target.value ? new Date(e.target.value) : undefined,
                                            }))
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select
                                    value={modalEmployee.status}
                                    onChange={(e) => setModalEmployee((p) => ({ ...p, status: e.target.value as EmployeeStatus }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="terminated">Terminated</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-black disabled:opacity-50 transition-colors"
                            >
                                {saving ? 'Saving...' : modalMode === 'add' ? 'Add Employee' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardWrapper>
    );
};

export default EmployeesPage;
